import { useSelector } from 'react-redux';
import {
  currentProviderContextSelector,
  currentOrganizationContextSelector,
  isDefaultOrgAccmeStaffSelector,
} from '../../store/user/selectors';

/**
 * Hook to determine if the current user has access to Client Credentials features.
 *
 * Access logic:
 * - When a provider context is selected:
 *   - User must be ACCME staff AND provider must have hasPasAPI enabled
 * - When no provider context (using organization context):
 *   - User is ACCME staff OR organization has hasPasAPI enabled
 *
 * This logic is used for:
 * - Showing/hiding the API Admin menu item in navigation
 * - Controlling access to the Client Credentials page
 *
 * @returns {boolean} Whether the user can access client credentials features
 */
export const useCanAccessClientCredentials = (): boolean => {
  const currentProviderContext = useSelector(currentProviderContextSelector);
  const currentOrganizationContext = useSelector(currentOrganizationContextSelector);
  const isAccmeStaff = useSelector(isDefaultOrgAccmeStaffSelector);

  let hasAccess: boolean;

  if (currentProviderContext) {
    // When provider context is selected: require BOTH staff status AND provider has PasAPI
    hasAccess = isAccmeStaff && currentProviderContext.hasPasAPI === true;
  } else {
    // When no provider context: original logic (ACCME staff OR org has PasAPI)
    hasAccess = isAccmeStaff || currentOrganizationContext?.hasPasAPI === true;
  }

  return hasAccess;
};
